import React, { useState, useEffect, forwardRef } from "react";
import {
    Container,
    Paper,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';

// --- Data Processing Utilities (Stays here) ---
const processReportData = (rawData) => {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
        return [];
    }
    const usersData = rawData.map(user => {
        let totalDurationHours = 0;
        const timePunches = user.timePunch.map(punch => {
            const startTime = new Date(punch.startTime);
            const endTime = punch.endTime ? new Date(punch.endTime) : null;
            let durationFormatted = 'N/A';
            let durationHours = 0;
            if (endTime) {
                const durationMs = endTime - startTime;
                durationHours = durationMs / (1000 * 60 * 60);
                const hours = Math.floor(durationHours);
                const minutes = Math.floor((durationHours - hours) * 60);
                durationFormatted = `${hours}h ${minutes}m`;
            } else {
                const now = new Date();
                const durationMs = now - startTime;
                durationHours = durationMs / (1000 * 60 * 60);
                const hours = Math.floor(durationHours);
                const minutes = Math.floor((durationHours - hours) * 60);
                durationFormatted = `**Clocked In** (${hours}h ${minutes}m so far)`;
            }
            totalDurationHours += durationHours;
            return {
                date: startTime.toLocaleDateString(),
                clockIn: startTime.toLocaleTimeString(),
                clockOut: endTime ? endTime.toLocaleTimeString() : '**Clocked In**',
                duration: durationFormatted,
            };
        });
        return {
            name: user.name,
            totalDurationHours: totalDurationHours,
            punches: timePunches,
        };
    });
    return usersData;
};

const processChartData = (usersData) => {
    return usersData.map(user => ({
        name: user.name,
        'Total Hours': parseFloat(user.totalDurationHours.toFixed(2))
    }));
};

export const ClockInReportPage = forwardRef(({ rawData }, ref) => {
    const [reportData, setReportData] = useState([]);
    const [chartData, setChartData] = useState([]);

    // useEffect(() => {
    //     if (rawData && rawData.length > 0) {
    //         const processedUsersData = processReportData(rawData);
    //         const processedChartData = processChartData(processedUsersData);
    //         setReportData(processedUsersData);
    //         setChartData(processedChartData);
    //     } else {
    //         setReportData([]);
    //         setChartData([]);
    //     }
    // }, [rawData]);

    useEffect(() => {
    let dataToUse = rawData;

    // ✅ If no rawData prop, try to load from sessionStorage (for new tab view)
    if (!dataToUse || dataToUse.length === 0) {
        const stored = sessionStorage.getItem("clockInReportData");
        if (stored) {
            const parsed = JSON.parse(stored);
            dataToUse = parsed.rawData || [];
        }
    }

    if (dataToUse && dataToUse.length > 0) {
        const processedUsersData = processReportData(dataToUse);
        const processedChartData = processChartData(processedUsersData);
        setReportData(processedUsersData);
        setChartData(processedChartData);
    } else {
        setReportData([]);
        setChartData([]);
    }
}, [rawData]);

    if (reportData.length === 0) {
        return (
            <Paper elevation={3} sx={{ p: 3, my: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    No data available for this report.
                </Typography>
            </Paper>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }} ref={ref}>
            {/* Visual Summary (Graph) */}
            <div style={{ pageBreakAfter: 'always' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 4 }}>

                {/* Section 1: Visual Summary (Graph) - Using Recharts */}
                <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '450px', backgroundColor: '#f5faff' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Visual Summary</Typography>
                    <Box sx={{ minHeight: '300px', height: '100%', maxHeight: '400px' }}>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                                data={chartData}
                                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                                <Tooltip />
                                <Legend />
                                <Bar dataKey="Total Hours" fill="#1976d2" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>

                {/* Employee Summary (Table) */}
                <Paper elevation={3} sx={{ p: 3, flexGrow: 1, minWidth: '450px', backgroundColor: '#e1f5fe' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Employee Summary</Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Total Hours</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reportData.map((user, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            '&:nth-of-type(odd)': { backgroundColor: '#f8f8f8' },
                                            '&:hover': { backgroundColor: '#e0e0e0', cursor: 'pointer' }
                                        }}
                                    >
                                        <TableCell>{user.name}</TableCell>
                                        <TableCell>{user.totalDurationHours.toFixed(2)} hrs</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
            </div>

            {/* Section 3: Detailed Clock-in Logs (Fully Open) */}
            <Box sx={{ mt: 2 }}>
                <Paper elevation={3} sx={{ p: 3, backgroundColor: '#f3e5f5' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Detailed Clock-in Logs</Typography>
                    {reportData.map((user, userIndex) => (
                        <div key={userIndex} style={{ pageBreakInside: 'avoid' }}>
                            <Box key={userIndex} sx={{ my: 3 }}>
                                <Typography variant="subtitle1" fontWeight="bold" color="primary.dark" sx={{ mb: 1, borderBottom: '1px solid #ce93d8' }}>
                                    {user.name}
                                </Typography>
                                <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 1 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: '#e1bee7' }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Clock-in</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Clock-out</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Duration</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {user.punches.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center">No punches found for this employee.</TableCell>
                                                </TableRow>
                                            ) : (
                                                user.punches.map((punch, punchIndex) => (
                                                    <TableRow
                                                        key={punchIndex}
                                                        sx={{
                                                            '&:nth-of-type(odd)': { backgroundColor: '#f8f8f8' },
                                                            '&:hover': { backgroundColor: '#e0e0e0', cursor: 'pointer' }
                                                        }}
                                                    >
                                                        <TableCell>{punch.date}</TableCell>
                                                        <TableCell>{punch.clockIn}</TableCell>
                                                        <TableCell sx={{ color: punch.clockOut.includes('Clocked In') ? 'error.main' : 'inherit', fontWeight: punch.clockOut.includes('Clocked In') ? 'bold' : 'normal' }}>{punch.clockOut}</TableCell>
                                                        <TableCell sx={{ color: punch.duration.includes('Clocked In') ? 'error.main' : 'inherit' }}>{punch.duration}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        </div>
                    ))}
                </Paper>
            </Box>
        </Container>
    );
});